import {
  checkIfEntityClasicObj,
  checkIfObjectEmpty,
} from '@common/lib/checkIfObjectEmpty';
import {
  type ChestDataType,
  type ContractDataType,
  type ContractRewardType,
  type ContractType,
  type MainPageContractTabs,
  type PaidSpecContractType,
  type ProductOfDayDataType,
  type SpecContractType,
} from '@common/types/contract';
import { type RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';

import {
  specContractRequirenments,
  validateSpecContractVisibilityForTab,
} from '@/common/config/contract';
import { goodsList } from '@/common/constants';
import { specContractList } from '@/common/constants/contract';
import { checkExpiredStatus } from '@/common/lib/contract/index';
import { getContractExtraStarPoints } from '@/common/lib/getContractExtaStarPoints';
import { type ContractData } from '@/common/types/contract';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { type MaterialDetailsType } from '@/common/types/userType';
import { getStatusBySpecContractData } from '@/components/Contract/lib/getStusByContractData';

import { selectTutorialByKind } from './userSelectors';

const selectChestData = (state: RootState): ChestDataType | undefined =>
  state.contract.chest?.data;
const selectContractList = (state: RootState): ContractDataType[] | null =>
  state.contract.list;

const selectNextSpecContractTimes = (
  state: RootState,
): Record<SpecContractType, number | null | undefined> => ({
  Custom: state.contract.nextCustomContractTime,
  Golden: state.contract.nextGoldContractTime,
  Grand: state.contract.nextGrandContractTime,
  Blitz: state.contract.nextBlitzContractTime,
});

const selectSpecContractDataByKind = createSelector(
  selectContractList,
  (_: RootState, kind: ContractType): ContractType => kind,
  (contractList, kind): ContractDataType | undefined =>
    contractList?.find(({ type }) => kind === type),
);

const selectTimeToNextSpecContractByKind = createSelector(
  [
    selectNextSpecContractTimes,
    (_: RootState, kind: SpecContractType): SpecContractType => kind,
  ],
  (timers, kind) => timers[kind],
);

const filterContractListByKind = createSelector(
  selectContractList,
  (_: RootState, kind: ContractType): ContractType => kind,
  (contractList, kind): ContractDataType[] | undefined =>
    contractList?.filter(({ type }) => kind === type),
);

const selectSortedByGoodsContracts = createSelector(
  filterContractListByKind,
  (contractList) =>
    contractList
      ? [...contractList].sort((a, b) => {
          const goodsListSet = new Set(goodsList);
          const aHasGoods = goodsListSet.has(Object.keys(a.data)[0]);
          const bHasGoods = goodsListSet.has(Object.keys(b.data)[0]);

          if (aHasGoods && !bHasGoods) {
            return -1;
          }

          if (bHasGoods && !aHasGoods) {
            return 1;
          }

          return 0;
        })
      : undefined,
);

const selectSpecContractTermsByKind = createSelector(
  selectSpecContractDataByKind,
  (contractData): ContractData | undefined => contractData?.data,
);

const selectSpecContractIsOpenByKind = createSelector(
  selectSpecContractDataByKind,
  (contractData): boolean => {
    if (!contractData) return false;

    return (
      !!contractData.isOpened &&
      typeof contractData.timeLeft === 'number' &&
      contractData.timeLeft > 0
    );
  },
);

const selectSpecContractOpenDateByKind = createSelector(
  selectSpecContractDataByKind,
  (contractData): string | undefined => contractData?.isOpened,
);

const selectSpecContractIdByKind = createSelector(
  selectSpecContractDataByKind,
  (contractData): string | undefined => contractData?.id,
);

const selectSpecContractTimeLeftByKind = createSelector(
  selectSpecContractDataByKind,
  (contractData): number | undefined => contractData?.timeLeft,
);

const selectProductOfDayContracts = (
  state: RootState,
): ProductOfDayDataType[] | null => state.contract.productOfDay;

const selectSpecContractRewardsByKind = createSelector(
  selectSpecContractDataByKind,
  selectProductOfDayContracts,
  (
    contractData,
    productOfDayContracts,
  ):
    | { item: ContractRewardType; value: number; extraPoints?: number }[]
    | undefined => {
    if (!contractData) {
      return;
    }

    const extraPoints = getContractExtraStarPoints(
      contractData,
      productOfDayContracts || [],
    );

    const rewards = ['cash', 'point', 'dice', 'lotteryTickets'] as const;

    return rewards
      .map((field) => {
        const reward = field in contractData ? contractData[field] || 0 : 0;
        const value =
          typeof reward === 'number' ? reward : Object.values(reward)[0];
        const item = (
          typeof reward === 'number' ? field : Object.keys(reward)[0]
        ) as ContractRewardType;

        return {
          item,
          value,
          extraPoints:
            item === 'point' && extraPoints ? extraPoints : undefined,
        };
      })
      .filter(({ value }) => !!value);
  },
);

const selectActiveBlitzContractStep = createSelector(
  selectSpecContractDataByKind,
  (contract): number | undefined => contract?.activeStep,
);

const selectSpecContractOpeningTimeEnd = createSelector(
  selectSpecContractDataByKind,
  (contract): string | undefined => contract?.openedEndAt,
);

const selectSpecContractExpiredStatusEndDate = createSelector(
  selectSpecContractDataByKind,
  (contract): string | undefined => {
    if (!contract?.contractTimerExtensionDuration || !contract.openedEndAt) {
      return;
    }

    const expiredStatusEndTime =
      new Date(contract.openedEndAt).getTime() +
      contract.contractTimerExtensionDuration * 1000;

    return new Date(expiredStatusEndTime).toISOString();
  },
);

const checkContractCompletion = createSelector(
  (
    state: RootState,
  ): Partial<Record<string, MaterialDetailsType>> | undefined =>
    state.user.user?.storage,
  (_: RootState, requirements?: ContractData): ContractData | undefined =>
    requirements,
  (storage, requirements): boolean => {
    if (!storage || !requirements) return false;

    return Object.entries(requirements).every(
      ([item, { amount }]) =>
        item in storage && (storage[item]?.value || 0) >= amount,
    );
  },
);

const selectSpecContractIsFail = createSelector(
  selectSpecContractDataByKind,
  (contractData): boolean => {
    if (!contractData) return false;

    const isContractExpired = checkExpiredStatus(contractData);

    return (
      !!contractData.isOpened &&
      typeof contractData.timeLeft === 'number' &&
      contractData.timeLeft <= 0 &&
      !isContractExpired
    );
  },
);

const selectSpecContractIsExpired = createSelector(
  selectSpecContractDataByKind,
  checkExpiredStatus,
);

const selectChestRewards = createSelector(
  selectChestData,
  (
    chestData,
  ):
    | {
        kind: string;
        value: number;
      }[]
    | undefined =>
    chestData &&
    Object.entries(chestData)
      .flatMap(([field, value]) => {
        const typeValue = value as number | Partial<Record<string, number>>;

        if (
          typeValue === 0 ||
          (typeof typeValue === 'object' && checkIfObjectEmpty(typeValue))
        ) {
          return;
        }

        const isObject = checkIfEntityClasicObj(typeValue);

        if (isObject) {
          const objectEntity = typeValue as Partial<Record<string, number>>;

          return Object.entries(objectEntity).map(([name, value]) => ({
            kind: name,
            value,
          }));
        }

        return {
          kind:
            field === 'tgem'
              ? 'tgem'
              : field === 'casinoTickets'
                ? 'chip'
                : field,
          value,
        };
      })
      .filter((item) => !!item),
);

const selectContractItemList = createSelector(
  selectContractList,
  (contracts: ContractDataType[] | null): string[] | undefined => {
    if (contracts) {
      const itemsList = new Set<string>();
      contracts.forEach((contract) => {
        if (!contract.rejectedAt) {
          Object.keys(contract.data || {}).forEach((name) => {
            itemsList.add(name);
          });
        }
      });

      return [...itemsList.values()];
    }
  },
);

const selectVisibleSpecialContractsCount = createSelector(
  [
    (state: RootState): boolean => state.user.isFirstVisit ?? false,
    (_state: RootState, userLevel: number): number => userLevel,
    (
      state: RootState,
    ): Array<{
      contract: ContractDataType | undefined;
      aboutInfoWasRead: boolean;
      reqLevel: number;
    }> =>
      specContractList.map((kind) => ({
        contract: selectSpecContractDataByKind(state, kind),
        aboutInfoWasRead: selectTutorialByKind(state, kind),
        reqLevel: specContractRequirenments[kind].level,
      })),
  ],
  (isFirstVisit, userLevel, contractsInfo): number => {
    let count = 0;

    contractsInfo.forEach(({ contract, aboutInfoWasRead, reqLevel }) => {
      const status = getStatusBySpecContractData(contract);

      if (userLevel > 1) {
        if (!aboutInfoWasRead && !contract && userLevel < reqLevel) {
          count++;
        }
      }

      if (!isFirstVisit) {
        if (
          contract &&
          contract?.type !== 'Default' &&
          status &&
          validateSpecContractVisibilityForTab(contract.type, status, 'special')
        ) {
          count++;
        }
      }
    });

    return count;
  },
);

const selectProductOfDayByGoodsType = createSelector(
  (state: RootState) => state.contract.productOfDay,
  (
    _: RootState,
    goodsType: GoodsType | ResourceTypeText | PlantTypeText | string,
  ): GoodsType | ResourceTypeText | PlantTypeText | string => goodsType,
  (productsOfDay, goodsType): ProductOfDayDataType | undefined => {
    return productsOfDay?.find((product) => product.name === goodsType);
  },
);

const selectProductOfDayById = createSelector(
  (state: RootState) => state.contract.productOfDay,
  (_: RootState, productId: string): string => productId,
  (productsOfDay, productId): ProductOfDayDataType | undefined => {
    return productsOfDay?.find((product) => product.id === productId);
  },
);

const selectVisibleProductsOfDayToClaim = createSelector(
  (state: RootState) => state.contract.productOfDay,
  (productsOfDay): ProductOfDayDataType[] => {
    return (productsOfDay ?? []).filter(
      (product) =>
        product.progress >= product.countToReward && !product.chestBoxOpenedAt,
    );
  },
);

const selectNotUpgradedProductOfDay = createSelector(
  selectProductOfDayContracts,
  (contracts): ProductOfDayDataType | undefined =>
    contracts?.find(
      ({ type, id, chestBoxOpenedAt }) =>
        (type === 'regular' || type === 'vip') &&
        id !== '-' &&
        !chestBoxOpenedAt,
    ),
);

const selectNotUpgradedProductOfDayList = createSelector(
  selectProductOfDayContracts,
  (contracts): ProductOfDayDataType[] | undefined =>
    contracts?.filter(
      ({ type, id, chestBoxOpenedAt }) =>
        (type === 'regular' || type === 'vip') &&
        id !== '-' &&
        !chestBoxOpenedAt,
    ),
);

const selectContractById = createSelector(
  selectContractList,
  (_: RootState, contractId?: string | null) => contractId,
  (contractlist, contractId): ContractDataType | undefined =>
    contractId && contractlist
      ? contractlist.find(({ id }) => id === contractId)
      : undefined,
);

const selectContractsByKindsOfSortedArray = createSelector(
  selectContractList,
  (_: RootState, kindsArray: ContractType[]): ContractType[] => kindsArray,
  (contractList, kindsArray) =>
    kindsArray
      .map((kind) => contractList?.find(({ type }) => type === kind))
      .filter((contract) => !!contract),
);

const selectSortedAvailableSpecContract = createSelector(
  selectContractList,
  (
    _: RootState,
    kindsArray: ContractType[],
    tab: MainPageContractTabs,
  ): { kindsArray: ContractType[]; tab: MainPageContractTabs } => ({
    kindsArray,
    tab,
  }),
  (contractList, { kindsArray, tab }) => {
    const filteredContractList = kindsArray
      .map((kind) => contractList?.find(({ type }) => type === kind))
      .filter((contract) => !!contract);

    return filteredContractList.filter((contract) => {
      const status = getStatusBySpecContractData(contract);

      if (!status) {
        return false;
      }

      const kind = contract.type as SpecContractType | PaidSpecContractType;

      return validateSpecContractVisibilityForTab(kind, status, tab);
    });
  },
);

const selectSortedExpiredSpecContract = createSelector(
  selectContractList,
  (_: RootState, kindsArray: ContractType[]): ContractType[] => kindsArray,
  (contractList, kindsArray) => {
    const filteredContractList = kindsArray
      .map((kind) => contractList?.find(({ type }) => type === kind))
      .filter((contract) => !!contract);

    return filteredContractList.filter((contract) => {
      const status = getStatusBySpecContractData(contract);

      return status === 'expired';
    });
  },
);

const selectSpecContractBaseExtentionTime = createSelector(
  selectSpecContractDataByKind,
  (contract): number | undefined =>
    contract?.contractTimerExtensionLimitTimeSinceOpened,
);

const selectSpecContractPriceForExtentionTime = createSelector(
  selectSpecContractDataByKind,
  (contract) => contract?.contractTimerExtensionCost,
);

export {
  checkContractCompletion,
  filterContractListByKind,
  selectActiveBlitzContractStep,
  selectChestRewards,
  selectContractById,
  selectContractItemList,
  selectContractsByKindsOfSortedArray,
  selectNotUpgradedProductOfDay,
  selectNotUpgradedProductOfDayList,
  selectProductOfDayByGoodsType,
  selectProductOfDayById,
  selectProductOfDayContracts,
  selectSortedAvailableSpecContract,
  selectSortedByGoodsContracts,
  selectSortedExpiredSpecContract,
  selectSpecContractBaseExtentionTime,
  selectSpecContractDataByKind,
  selectSpecContractExpiredStatusEndDate,
  selectSpecContractIdByKind,
  selectSpecContractIsExpired,
  selectSpecContractIsFail,
  selectSpecContractIsOpenByKind,
  selectSpecContractOpenDateByKind,
  selectSpecContractOpeningTimeEnd,
  selectSpecContractPriceForExtentionTime,
  selectSpecContractRewardsByKind,
  selectSpecContractTermsByKind,
  selectSpecContractTimeLeftByKind,
  selectTimeToNextSpecContractByKind,
  selectVisibleProductsOfDayToClaim,
  selectVisibleSpecialContractsCount,
};
